import "../styles/Consommation.css";
import { useState, useEffect } from "react";
import axios from "axios";
import { FaEye, FaPlus, FaTrash, FaEdit } from "react-icons/fa";
import { API } from "../config/api";

export default function Consommation() {
  const [consommations, setConsommations] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [showFormId, setShowFormId] = useState(null);
  const [editingConsommation, setEditingConsommation] = useState(null);
  const [filter, setFilter] = useState({ nom: '', periode: '', contrat: '', secteur: '' });
  
  // For dropdown filters
  const [uniqueClients, setUniqueClients] = useState([]);
  const [uniquePeriods, setUniquePeriods] = useState([]);
  const [uniqueContracts, setUniqueContracts] = useState([]);
  const [uniqueSecteurs, setUniqueSecteurs] = useState([]);

  const [form, setForm] = useState({
    consommationActuelle: "",
    consommationPre: 0,
    periode: "",
    jours: 30,
    idTech: "",
    idTranch: "",
    idCont: "",
    status: "non fait",
  });

  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user")) || {};

  const getAdaptiveStatus = (periode, hasConsumptionData = false, contractId = null) => {
    if (!periode) return "init";
    
    // If this is the first time creating consumption (no data entered yet)
    if (!hasConsumptionData) {
      return "init";
    }
    
    // Get current month for comparison
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const currentPeriod = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;
    
    // Get the most recent consumption period for this contract
    let lastEnteredPeriod = null;
    if (contractId) {
      const contractConsommations = consommations
        .filter((c) => c.idcont === contractId && c.status !== "initial" && c.periode)
        .sort((a, b) => b.periode.localeCompare(a.periode));
      
      lastEnteredPeriod = contractConsommations.length > 0 ? contractConsommations[0].periode : null;
    }
    
    // If the last entered period equals the current month, mark as "à jour"
    if (lastEnteredPeriod === currentPeriod) {
      return "à jour";
    }
    
    // If we have consumption history but it's not current month, mark as "à faire"
    if (lastEnteredPeriod) {
      return "à faire";
    }
    
    // Fallback logic: check if this period is current month
    if (periode === currentPeriod) {
      return "à faire"; // Current period where consumption entry is needed
    } else {
      return "à jour"; // Past or future periods that are completed/up to date
    }
  };

  const fetchConsommations = async () => {
    try {
      const res = await axios.get(API + "/api/consommations", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConsommations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur récupération consommations:", err);
      setConsommations([]);
    }
  };

  const fetchContracts = async () => {
    try {
      const res = await axios.get(API + "/api/contracts", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setContracts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur récupération contrats:", err);
      setContracts([]);
    }
  };

  const fetchSecteurs = async () => {
    try {
      const res = await axios.get(API + "/api/secteurs", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const secteurs = Array.isArray(res.data) ? res.data : [];
      setUniqueSecteurs(secteurs.map(s => ({ id: s.ids, nom: s.nom })));
    } catch (err) {
      console.error("Erreur récupération secteurs:", err);
      setUniqueSecteurs([]);
    }
  };

  useEffect(() => {
    const init = async () => {
      await fetchConsommations();
      await fetchContracts();
      await fetchSecteurs();
    };
    init();
  }, []);

  // Update unique values when data changes
  useEffect(() => {
    if (contracts.length > 0) {
      const clients = [...new Set(contracts.map(c => `${c.nomclient} ${c.prenomclient}`))].sort();
      const contractCodes = [...new Set(contracts.map(c => c.codeqr))].sort();
      setUniqueClients(clients);
      setUniqueContracts(contractCodes);
    }
  }, [contracts]);

  useEffect(() => {
    if (consommations.length > 0) {
      const periods = [...new Set(consommations.map(c => c.periode).filter(p => p))].sort((a, b) => b.localeCompare(a));
      setUniquePeriods(periods);
    }
  }, [consommations]);

  const getLastConsommation = (idcontract) => {
    // Get all consumptions for this contract (exclude 'initial' status)
    const contractConsommations = consommations
      .filter((c) => c.idcont === idcontract && c.status !== "initial")
      .sort((a, b) => b.periode.localeCompare(a.periode));

    // Return the most recent consumption (last paid period)
    return contractConsommations[0] || null;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // If the period is changed, validate if it's already recorded
    if (name === "periode" && showFormId) {
      const { recordedMonths, lastPaidPeriod, contractStartPeriod } =
        getAvailableMonths(showFormId);

      console.log("Checking period validation:", {
        selectedPeriod: value,
        recordedMonths,
        isIncluded: recordedMonths.includes(value),
      });

      if (recordedMonths.includes(value)) {
        alert(
          `⚠️ Une consommation pour la période ${value} a déjà été saisie pour ce contrat.`
        );
        return; // Don't update the form if the month is already recorded
      }

      // Additional validation: Check if the selected period is within allowed range
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth() + 1;
      const currentYear = currentDate.getFullYear();
      const currentPeriod = `${currentYear}-${currentMonth
        .toString()
        .padStart(2, "0")}`;

      // Check if period is before contract start
      if (value < contractStartPeriod) {
        alert(
          `⚠️ Vous ne pouvez pas saisir une consommation avant le début du contrat (${contractStartPeriod}).`
        );
        return;
      }

      if (lastPaidPeriod === null) {
        // No payments made - allow any month from contract start up to current month
        if (value > currentPeriod) {
          alert(
            `⚠️ Vous pouvez seulement saisir des consommations jusqu'au mois actuel.`
          );
          return;
        }
      } else {
        // Payments exist - calculate allowed range from next month after latest paid
        const [latestPaidYear, latestPaidMonth] = lastPaidPeriod
          .split("-")
          .map(Number);

        let nextYear = latestPaidYear;
        let nextMonth = latestPaidMonth + 1;

        if (nextMonth > 12) {
          nextMonth = 1;
          nextYear++;
        }

        const nextPeriodAfterPaid = `${nextYear}-${nextMonth
          .toString()
          .padStart(2, "0")}`;

        // Check if selected period is within allowed range
        if (value < nextPeriodAfterPaid || value > currentPeriod) {
          alert(
            `⚠️ Vous pouvez seulement saisir des consommations entre ${nextPeriodAfterPaid} et ${currentPeriod}.`
          );
          return;
        }
      }

      updatePreviousConsumption(showFormId, value);
    }

    setForm({ ...form, [name]: value });
  };

  const updatePreviousConsumption = async (idCont, selectedPeriod) => {
    try {
      console.log("🔍 updatePreviousConsumption called with:", {
        idCont,
        selectedPeriod,
      });

      // Get ALL consumptions for this contract before the selected period
      const allConsumptionsBefore = consommations
        .filter(
          (c) =>
            c.idcont === idCont &&
            c.periode < selectedPeriod &&
            c.status !== "initial"
        )
        .sort((a, b) => b.periode.localeCompare(a.periode));

      console.log(
        "🔍 All consumptions before selected period:",
        allConsumptionsBefore
      );

      // Use the MOST RECENT consumption (first one after sorting)
      if (allConsumptionsBefore.length > 0) {
        const lastConsumption = allConsumptionsBefore[0];
        console.log("🔍 Using most recent consumption:", lastConsumption);
        
        setForm((prev) => ({
          ...prev,
          consommationPre: lastConsumption.consommationactuelle || 0,
        }));
      } else {
        // No previous consumption found, set to 0
        console.log("🔍 No previous consumptions found");
        setForm((prev) => ({
          ...prev,
          consommationPre: 0,
        }));
      }
    } catch (err) {
      console.error("Error updating previous consumption:", err);
    }
  };

  const getAvailableMonths = (idCont) => {
    // Get current date info
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();

    // Find the contract to get start date
    const contract = contracts.find((c) => c.idcontract === idCont);
    const contractStartDate = contract
      ? new Date(contract.datedebut)
      : new Date();
    const contractStartYear = contractStartDate.getFullYear();
    const contractStartMonth = contractStartDate.getMonth() + 1;
    const contractStartPeriod = `${contractStartYear}-${contractStartMonth
      .toString()
      .padStart(2, "0")}`;

    // Get all recorded periods for this contract (exclude 'initial' status)
    const recordedPeriods = consommations
      .filter((c) => c.idcont === idCont && c.status !== "initial")
      .map((c) => c.periode)
      .filter((p) => p); // Remove null/undefined periods

    console.log("Contract start period:", contractStartPeriod);
    console.log(
      "All consumptions for contract:",
      consommations.filter((c) => c.idcont === idCont)
    );
    console.log(
      "Recorded periods for contract (excluding initial):",
      recordedPeriods
    );

    const currentPeriod = `${currentYear}-${currentMonth
      .toString()
      .padStart(2, "0")}`;

    // Check if there are any paid consumptions
    if (recordedPeriods.length === 0) {
      // No consumptions paid - show all months from contract start to current month
      console.log(
        "🗓️ No consumptions paid - showing all months from contract start"
      );

      const allAvailableMonths = [];

      // Start from contract start date
      let iterYear = contractStartYear;
      let iterMonth = contractStartMonth;

      while (
        iterYear < currentYear ||
        (iterYear === currentYear && iterMonth <= currentMonth)
      ) {
        const period = `${iterYear}-${iterMonth.toString().padStart(2, "0")}`;
        allAvailableMonths.push(period);

        // Move to next month
        iterMonth++;
        if (iterMonth > 12) {
          iterMonth = 1;
          iterYear++;
        }
      }

      console.log(
        "All available months from contract start:",
        allAvailableMonths
      );

      // Sort in descending order (newest first)
      const availableMonths = allAvailableMonths.sort((a, b) =>
        b.localeCompare(a)
      );

      return {
        recordedMonths: recordedPeriods,
        currentMonth: currentPeriod,
        lastPaidPeriod: null,
        availableMonths,
        contractStartPeriod,
      };
    }

    // Find the latest (most recent) paid period
    const sortedRecordedPeriods = recordedPeriods.sort((a, b) =>
      b.localeCompare(a)
    );
    const latestPaidPeriod = sortedRecordedPeriods[0];

    console.log("🗓️ Latest paid period:", latestPaidPeriod);

    // Parse the latest paid period
    const [latestPaidYear, latestPaidMonth] = latestPaidPeriod
      .split("-")
      .map(Number);

    // Calculate the next month after the latest paid period
    let nextYear = latestPaidYear;
    let nextMonth = latestPaidMonth + 1;

    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear++;
    }

    const nextPeriodAfterPaid = `${nextYear}-${nextMonth
      .toString()
      .padStart(2, "0")}`;

    console.log(
      "🗓️ Period range: from",
      nextPeriodAfterPaid,
      "to",
      currentPeriod
    );

    // Generate available months from next month after latest paid to current month
    const allPossibleMonths = [];

    // Start from the month after the latest paid
    let iterYear = nextYear;
    let iterMonth = nextMonth;

    while (
      iterYear < currentYear ||
      (iterYear === currentYear && iterMonth <= currentMonth)
    ) {
      const monthStr = iterMonth.toString().padStart(2, "0");
      const period = `${iterYear}-${monthStr}`;
      allPossibleMonths.push(period);

      // Move to next month
      iterMonth++;
      if (iterMonth > 12) {
        iterMonth = 1;
        iterYear++;
      }
    }

    console.log(
      "All possible months (from next after paid):",
      allPossibleMonths
    );

    // Filter out already recorded months and sort in descending order (newest first)
    const availableMonths = allPossibleMonths
      .filter((period) => !recordedPeriods.includes(period))
      .sort((a, b) => b.localeCompare(a)); // Sort descending (newest first)

    console.log("Available months (filtered):", availableMonths);

    return {
      recordedMonths: recordedPeriods,
      currentMonth: currentPeriod,
      lastPaidPeriod: latestPaidPeriod,
      availableMonths,
      contractStartPeriod,
    };
  };

  const handleAddClick = async (idCont) => {
    try {
      const res = await axios.get(API + `/api/consommations/last/${idCont}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { availableMonths } = getAvailableMonths(idCont);
      // Use the first available month (newest) as default since they're sorted descending
      const defaultPeriod =
        availableMonths.length > 0
          ? availableMonths[0]
          : new Date().toISOString().slice(0, 7);

      setForm({
        consommationActuelle: "",
        consommationPre: res.data?.consommationactuelle || 0,
        periode: defaultPeriod,
        jours: 30,
        idTech: user?.idtech || null,
        idTranch: "",
        idCont,
        status: "non fait",
      });

      setShowFormId(idCont);
      setEditingConsommation(null);

      // Update previous consumption based on the selected period
      setTimeout(() => {
        updatePreviousConsumption(idCont, defaultPeriod);
      }, 100);
    } catch (err) {
      console.log("Aucune consommation précédente");
      const { availableMonths } = getAvailableMonths(idCont);
      // Use the first available month (newest) as default since they're sorted descending
      const defaultPeriod =
        availableMonths.length > 0
          ? availableMonths[0]
          : new Date().toISOString().slice(0, 7);

      setForm({
        consommationActuelle: "",
        consommationPre: 0,
        periode: defaultPeriod,
        jours: 30,
        idTech: user?.idtech || null,
        idTranch: "",
        idCont,
        status: "non fait",
      });

      setShowFormId(idCont);
      setEditingConsommation(null);

      // Update previous consumption based on the selected period
      setTimeout(() => {
        updatePreviousConsumption(idCont, defaultPeriod);
      }, 100);
    }
  };

  const handleEditClick = (consommation) => {
    setEditingConsommation(consommation);
    setForm({
      consommationActuelle: consommation.consommationactuelle,
      consommationPre: consommation.consommationpre,
      periode: consommation.periode,
      jours: consommation.jours || 30,
      idTech: consommation.idtech || user?.idtech || null,
      idTranch: consommation.idtranch || "",
      idCont: consommation.idcont,
      status: consommation.status,
    });
    setShowFormId(consommation.idcont);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (parseInt(form.consommationActuelle) < parseInt(form.consommationPre)) {
      alert(
        "❌ La consommation actuelle doit être supérieure ou égale à la précédente."
      );
      return;
    }

    // Only check for existing consumptions if we're creating, not editing
    if (!editingConsommation) {
      // Check if consumption already exists for this contract and period
      const existingConsommations = consommations.filter(
        (c) =>
          c.idcont === form.idCont &&
          c.periode === form.periode &&
          c.status !== "initial"
      );

      console.log("🔍 Checking existing consumptions:", {
        contractId: form.idCont,
        period: form.periode,
        existingConsommations,
        allConsommationsForContract: consommations.filter(
          (c) => c.idcont === form.idCont
        ),
      });

      if (existingConsommations.length > 0) {
        alert(
          `⚠️ Une consommation pour la période ${form.periode} a déjà été saisie pour ce contrat.`
        );
        return;
      }
    }

    // Additional validation: Check if the selected period is within allowed range (updated logic)
    const { lastPaidPeriod, contractStartPeriod } = getAvailableMonths(
      form.idCont
    );
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear();
    const currentPeriod = `${currentYear}-${currentMonth
      .toString()
      .padStart(2, "0")}`;

    // Check if period is before contract start
    if (form.periode < contractStartPeriod) {
      alert(
        `❌ Vous ne pouvez pas saisir une consommation avant le début du contrat (${contractStartPeriod}).`
      );
      return;
    }

    if (lastPaidPeriod === null) {
      // No payments made - allow any month from contract start up to current month
      if (form.periode > currentPeriod) {
        alert(
          `❌ Vous pouvez seulement saisir des consommations jusqu'au mois actuel.`
        );
        return;
      }
    } else {
      // Payments exist - calculate allowed range from next month after latest paid
      const [latestPaidYear, latestPaidMonth] = lastPaidPeriod
        .split("-")
        .map(Number);

      let nextYear = latestPaidYear;
      let nextMonth = latestPaidMonth + 1;

      if (nextMonth > 12) {
        nextMonth = 1;
        nextYear++;
      }

      const nextPeriodAfterPaid = `${nextYear}-${nextMonth
        .toString()
        .padStart(2, "0")}`;

      // Check if selected period is within allowed range
      if (form.periode < nextPeriodAfterPaid || form.periode > currentPeriod) {
        alert(
          `❌ Vous pouvez seulement saisir des consommations entre ${nextPeriodAfterPaid} et ${currentPeriod}.`
        );
        return;
      }
    }

    try {
      // Check if consumption data has been entered (consommationActuelle > 0)
      const hasConsumptionData = parseInt(form.consommationActuelle) > 0;
      const adaptiveStatus = getAdaptiveStatus(form.periode, hasConsumptionData, form.idCont);
      
      const dataToSend = {
        ...form,
        consommationActuelle: parseInt(form.consommationActuelle),
        consommationPre: parseInt(form.consommationPre),
        jours: parseInt(form.jours),
        idTranch: null,
        status: adaptiveStatus,
      };

      let res;
      if (editingConsommation) {
        // Update existing consumption
        res = await axios.put(
          API + `/api/consommations/${editingConsommation.idcons}`,
          dataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert(
          `✅ Consommation modifiée.${res.data?.montantCalcule !== undefined 
            ? `\n💰 Montant calculé : ${parseFloat(res.data.montantCalcule).toFixed(2)} DH` 
            : ''}`
        );
      } else {
        // Create new consumption
        res = await axios.post(API + "/api/consommations", dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.montantCalcule !== undefined) {
          alert(
            `✅ Consommation enregistrée.\n💰 Montant calculé : ${parseFloat(
              res.data.montantCalcule
            ).toFixed(2)} DH`
          );
        } else {
          alert("✅ Consommation enregistrée.");
        }
      }

      fetchConsommations();
      setShowFormId(null);
      setEditingConsommation(null);
    } catch (err) {
      console.error("❌ Erreur enregistrement consommation:", err);
      alert("Erreur enregistrement consommation");
    }
  };

  const handleStatusUpdate = async (consommationId, currentStatus) => {
    let newStatus;
    
    // Determine the new status based on current status
    if (currentStatus === "init") {
      newStatus = "à faire"; // Move from init to needing consumption entry
    } else if (currentStatus === "à faire") {
      newStatus = "à jour"; // Mark as completed when consumption is entered
    } else if (currentStatus === "à jour") {
      newStatus = "à faire"; // Allow going back to needing entry if needed
    } else {
      return; // Don't change other statuses
    }

    if (!window.confirm(`Changer le statut vers "${newStatus}" ?`)) {
      return;
    }

    try {
      await axios.patch(
        API + `/api/consommations/${consommationId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state
      setConsommations(prev => prev.map(c => 
        c.idcons === consommationId 
          ? { ...c, status: newStatus }
          : c
      ));
      
      alert(`✅ Statut mis à jour: ${newStatus}`);
    } catch (err) {
      console.error("Erreur mise à jour statut :", err);
      alert("❌ Impossible de mettre à jour le statut.");
    }
  };

  const handleDelete = async (id) => {
    try {
      // Get the consumption record
      const consumption = consommations.find(c => c.idcons === id);
      
      if (!consumption) {
        alert('❌ Consommation introuvable');
        return;
      }

      // Get all consumptions for this contract, sorted by date (newest first)
      const contractConsumptions = consommations
        .filter(c => c.idcont === consumption.idcont)
        .sort((a, b) => new Date(b.dateconso || b.date) - new Date(a.dateconso || a.date));

      // Check if this is the last (most recent) consumption
      if (contractConsumptions.length > 0 && contractConsumptions[0].idcons !== id) {
        alert('❌ Vous pouvez uniquement supprimer la dernière entrée de consommation!');
        return;
      }

      // Show confirmation with details
      const confirmMessage = `⚠️ Supprimer la dernière consommation et sa facture associée ?\n\nPériode: ${consumption.periode}\nConsommation: ${consumption.consommationactuelle} m³`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Delete associated invoice if it exists
      try {
        await axios.delete(API + `/api/factures/by-consumption/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('✅ Facture associée supprimée');
      } catch (err) {
        console.log('ℹ️ Pas de facture associée ou déjà supprimée');
      }
      
      // Delete the consumption record
      await axios.delete(API + `/api/consommations/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
        
      alert("✅ Consommation et facture supprimées avec succès");
      fetchConsommations();
    } catch (err) {
      console.error("Erreur suppression consommation :", err);
      alert("❌ Erreur lors de la suppression");
    }
  };

  const consommationsFiltres = (
    Array.isArray(consommations) ? consommations : []
  ).filter((c) => {
    // Filter out 'initial' status consumptions from display
    if (c.status === 'initial' || c.status === 'init') {
      return false;
    }
    
    const contrat = (Array.isArray(contracts) ? contracts : []).find(
      (ct) => ct.idcontract === c.idcont
    );
    const nomClient = `${contrat?.nomclient || ""} ${
      contrat?.prenomclient || ""
    }`;
    
    // Enhanced search - support partial text search for client name
    const matchNom = filter.nom === "" || 
      (filter.nom === nomClient) || 
      nomClient.toLowerCase().includes(filter.nom.toLowerCase());
    
    const matchPeriode = filter.periode === "" || c.periode === filter.periode;
    const matchContrat = filter.contrat === "" || contrat?.codeqr === filter.contrat;
    
    // Secteur matching - check secteur_id first, then ids, then idsecteur
    const secteurId = contrat?.secteur_id || contrat?.ids || contrat?.idsecteur;
    const secteurMatch = filter.secteur === "" || (secteurId ? secteurId.toString() === filter.secteur : false);
    
    return matchNom && matchPeriode && matchContrat && secteurMatch;
  });

  return (
    <div className="consommation-page">
      <h1>Gestion des Consommations</h1>
      <div className="filters">
        <input
          type="text"
          placeholder="Rechercher client..."
          value={filter.nom}
          onChange={(e) => setFilter({ ...filter, nom: e.target.value })}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0', 
            minWidth: '200px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
          title="Rechercher par nom de client"
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        />

        <select
          value={filter.secteur}
          onChange={(e) => setFilter({ ...filter, secteur: e.target.value })}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0',
            minWidth: '140px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            cursor: 'pointer'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <option value="">Tous les secteurs</option>
          {uniqueSecteurs.map(secteur => (
            <option key={secteur.id} value={secteur.id}>{secteur.nom}</option>
          ))}
        </select>

        <input
          type="month"
          value={filter.periode}
          onChange={(e) => setFilter({ ...filter, periode: e.target.value })}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0',
            minWidth: '140px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
          title="Sélectionner une période"
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        />

        <select
          value={filter.contrat}
          onChange={(e) => setFilter({ ...filter, contrat: e.target.value })}
          style={{ 
            padding: '0.75rem 1rem', 
            borderRadius: '10px', 
            border: '2px solid #e2e8f0',
            minWidth: '140px',
            fontSize: '0.875rem',
            background: 'white',
            transition: 'all 0.3s ease',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
            cursor: 'pointer'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = '#7c3aed';
            e.target.style.boxShadow = '0 0 0 3px rgba(124, 58, 237, 0.1)';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = '#e2e8f0';
            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.05)';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          <option value="">Tous les contrats</option>
          {uniqueContracts.map(contract => (
            <option key={contract} value={contract}>{contract}</option>
          ))}
        </select>
      </div>

      <div className="table-responsive">
        <table className="consommation-table">
          <thead>
            <tr>
              <th>Contrat</th>
              <th style={{ width: '90px', minWidth: '90px' }}>Client</th>
              <th>Secteur</th>
              <th>Période</th>
              <th>AncienIndex</th>
              <th>Nouveau</th>
              <th>Conso</th>
              <th>Montant (DH)</th>
              <th>Tech</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
          {(
            (filter.nom || filter.periode || filter.contrat || filter.secteur
              ? consommationsFiltres
              : contracts) || []
          ).map((item, i) => {
            const contrat =
              filter.nom || filter.periode || filter.contrat || filter.secteur
                ? contracts.find((c) => c.idcontract === item.idcont)
                : item;
            const lastConso =
              filter.nom || filter.periode || filter.contrat || filter.secteur
                ? item
                : getLastConsommation(item.idcontract);

            const volume =
              lastConso?.consommationactuelle &&
              lastConso?.consommationpre !== undefined
                ? lastConso.consommationactuelle - lastConso.consommationpre
                : null;

            // Get contract info for better display
            const contractInfo =
              filter.nom || filter.periode || filter.contrat || filter.secteur ? contrat : item;

            const { lastPaidPeriod } = getAvailableMonths(
              contractInfo?.idcontract
            );
            const displayPeriod = lastConso?.periode || lastPaidPeriod || "-";
            const displayPeriodFormatted =
              displayPeriod !== "-"
                ? new Date(displayPeriod + "-01").toLocaleDateString("fr-FR", {
                    year: "numeric",
                    month: "long",
                  })
                : "-";

            // Calculate adaptive status for this consumption
            const adaptiveStatus = lastConso?.periode 
              ? getAdaptiveStatus(
                  lastConso.periode, 
                  lastConso.consommationactuelle > 0, 
                  contractInfo?.idcontract
                )
              : null;

            return (
              <tr key={i}>
                <td>{contrat?.codeqr || contractInfo?.codeqr || "-"}</td>
                <td style={{ 
                  width: '90px', 
                  minWidth: '90px', 
                  fontSize: '0.75em',
                  padding: '2px 4px',
                  wordBreak: 'break-word'
                }}>
                  <div title={
                    contrat
                      ? `${contrat.nomclient} ${contrat.prenomclient}`
                      : contractInfo
                      ? `${contractInfo.nomclient} ${contractInfo.prenomclient}`
                      : "-"
                  }>
                    {contrat
                      ? `${contrat.nomclient} ${contrat.prenomclient}`
                      : contractInfo
                      ? `${contractInfo.nomclient} ${contractInfo.prenomclient}`
                      : "-"}
                  </div>
                </td>
                <td>
                  {(() => {
                    const secteurId = contrat?.secteur_id || contrat?.ids || contrat?.idsecteur || contractInfo?.secteur_id || contractInfo?.ids || contractInfo?.idsecteur;
                    const secteur = uniqueSecteurs.find(s => s.id === secteurId);
                    return secteur ? secteur.nom : "-";
                  })()}
                </td>
                <td
                  title={
                    displayPeriod !== "-"
                      ? `Période: ${displayPeriod}`
                      : "Aucune consommation"
                  }
                >
                  {displayPeriodFormatted}
                </td>
                <td>{lastConso?.consommationpre ?? "-"}</td>
                <td>{lastConso?.consommationactuelle ?? "-"}</td>
                <td>{volume !== null && volume >= 0 ? volume : "-"}</td>
                <td>
                  {lastConso?.montantcalcule !== undefined
                    ? parseFloat(lastConso.montantcalcule).toFixed(2)
                    : lastConso?.prix && volume !== null && volume >= 0
                    ? (volume * lastConso.prix).toFixed(2)
                    : "-"}
                </td>
                <td>{lastConso?.nomtech ?? "-"}</td>
                <td>
                  <span
                    style={{
                      color:
                        (adaptiveStatus || lastConso?.status) === "à jour"
                          ? "#28a745"
                          : (adaptiveStatus || lastConso?.status) === "à faire"
                          ? "#ffc107"
                          : (adaptiveStatus || lastConso?.status) === "init"
                          ? "#6c757d"
                          : "#666",
                      fontWeight: "bold",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      backgroundColor:
                        (adaptiveStatus || lastConso?.status) === "à jour"
                          ? "#d4edda"
                          : (adaptiveStatus || lastConso?.status) === "à faire"
                          ? "#fff3cd"
                          : (adaptiveStatus || lastConso?.status) === "init"
                          ? "#e9ecef"
                          : "transparent",
                      cursor: "default",
                      transition: "all 0.3s ease",
                    }}
                    title="Statut de consommation (lecture seule)"
                  >
                    {(adaptiveStatus || lastConso?.status) === "à jour" 
                      ? "✅ À jour"
                      : (adaptiveStatus || lastConso?.status) === "à faire"
                      ? "⚠️ À saisir"
                      : (adaptiveStatus || lastConso?.status) === "init"
                      ? "🔄 Init"
                      : lastConso ? "❓ Inconnu" : "Init"}
                  </span>
                </td>
                <td className="action-buttons">
                  {lastConso?.idcons && (
                    <>
                      <button onClick={() => setViewing(lastConso)}>
                        <FaEye />
                      </button>
                      {lastConso?.status === "init" && (
                        <button 
                          onClick={() => handleEditClick(lastConso)}
                          title="Modifier la consommation initiale"
                          style={{ backgroundColor: '#ffc107' }}
                        >
                          <FaEdit />
                        </button>
                      )}
                      <button onClick={() => handleDelete(lastConso.idcons)}>
                        <FaTrash />
                      </button>
                    </>
                  )}
                  <button
                    onClick={() =>
                      handleAddClick(
                        contractInfo?.idcontract || contrat?.idcontract
                      )
                    }
                  >
                    <FaPlus />
                  </button>
                </td>
              </tr>
            );
          })}
          {(filter.nom || filter.periode || filter.contrat || filter.secteur) &&
            consommationsFiltres.length === 0 && (
              <tr>
                <td colSpan="11">Aucune consommation trouvée</td>
              </tr>
            )}
        </tbody>
      </table>
    </div>

      {viewing && (
        <div className="consommation-modal-overlay">
          <div className="consommation-modal">
            <h2>Détail consommation</h2>
            <p>
              <strong>Client :</strong> {viewing.nomclient}
            </p>
            <p>
              <strong>Contrat :</strong> {viewing.codeqr}
            </p>
            <p>
              <strong>Période :</strong> {viewing.periode}
            </p>
            <p>
              <strong>Précédente :</strong> {viewing.consommationpre} m³
            </p>
            <p>
              <strong>Actuelle :</strong> {viewing.consommationactuelle} m³
            </p>
            <p>
              <strong>Consommation :</strong>{" "}
              {viewing.consommationactuelle - viewing.consommationpre} m³
            </p>
            <p>
              <strong>Technicien :</strong> {viewing.nomtech}
            </p>
            <p>
              <strong>Status :</strong> {viewing.status}
            </p>
            <div style={{ 
              marginTop: "10px", 
              padding: "8px", 
              backgroundColor: "#f8f9fa", 
              borderRadius: "4px",
              fontSize: "0.9em",
              color: "#6c757d"
            }}>
              <strong>Signification des statuts :</strong>
              <br />• 🔄 Init : Consommation créée pour la première fois
              <br />• ⚠️ À saisir : Mois actuel, saisie de consommation requise
              <br />• ✅ À jour : Consommation saisie et terminée
            </div>
            <p>
              <strong>Montant :</strong>{" "}
              {viewing.montantcalcule
                ? parseFloat(viewing.montantcalcule).toFixed(2) + " DH"
                : "-"}
            </p>
            <button className="close-button" onClick={() => setViewing(null)}>
              Fermer
            </button>
          </div>
        </div>
      )}

      {showFormId && (
        <div className="consommation-modal-overlay">
          <div className="consommation-modal">
            <h2>{editingConsommation ? 'Modifier' : 'Ajouter'} une consommation</h2>
            <form onSubmit={handleSubmit}>
              <p>
                <strong>Contrat :</strong>{" "}
                {contracts.find((c) => c.idcontract === showFormId)?.codeqr ||
                  "-"}
              </p>
              <p>
                <strong>Client :</strong>{" "}
                {contracts.find((c) => c.idcontract === showFormId)?.nomclient}{" "}
                {
                  contracts.find((c) => c.idcontract === showFormId)
                    ?.prenomclient
                }
              </p>

              {(() => {
                const { recordedMonths, availableMonths, lastPaidPeriod } =
                  getAvailableMonths(showFormId);
                const contract = contracts.find(
                  (c) => c.idcontract === showFormId
                );
                const contractStartDate = contract
                  ? new Date(contract.datedebut).toLocaleDateString("fr-FR")
                  : "";

                if (availableMonths.length === 0) {
                  const lastConsumption = getLastConsommation(showFormId);
                  return (
                    <div
                      style={{
                        padding: "10px",
                        backgroundColor: "#fff3cd",
                        borderRadius: "5px",
                        marginBottom: "15px",
                      }}
                    >
                      <p
                        style={{
                          color: "#856404",
                          fontSize: "0.9em",
                          fontWeight: "bold",
                          margin: "0 0 10px 0",
                        }}
                      >
                        ⚠️ Tous les mois disponibles ont déjà été enregistrés pour
                        ce contrat.
                      </p>
                      {lastPaidPeriod && lastConsumption && (
                        <div style={{ fontSize: "0.85em", color: "#666" }}>
                          <p style={{ margin: "5px 0" }}>
                            <strong>📅 Dernière période payée:</strong>{" "}
                            {new Date(lastPaidPeriod + "-01").toLocaleDateString(
                              "fr-FR",
                              {
                                year: "numeric",
                                month: "long",
                              }
                            )}
                          </p>
                          <p style={{ margin: "5px 0" }}>
                            <strong>📊 Dernière consommation:</strong>{" "}
                            {lastConsumption.consommationactuelle - lastConsumption.consommationpre} m³
                          </p>
                          <p style={{ margin: "5px 0" }}>
                            <strong>💰 Montant calculé:</strong>{" "}
                            {lastConsumption.montantcalcule
                              ? parseFloat(lastConsumption.montantcalcule).toFixed(2) + " DH"
                              : "-"}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                }

                // Display last paid period info if exists
                if (lastPaidPeriod) {
                  const lastPaidDate = new Date(
                    lastPaidPeriod + "-01"
                  ).toLocaleDateString("fr-FR", {
                    year: "numeric",
                    month: "long",
                  });
                  return (
                    <div
                      style={{
                        marginBottom: "15px",
                        padding: "10px",
                        backgroundColor: "#e8f5e8",
                        borderRadius: "5px",
                      }}
                    >
                      <p>
                        <strong>Dernière période payée:</strong> {lastPaidDate}
                      </p>

                      <p>
                        <strong>Relevé précédent :</strong>{" "}
                        {form.consommationPre} m³
                      </p>
                    </div>
                  );
                } else {
                  return (
                    <div
                      style={{
                        marginBottom: "15px",
                        padding: "10px",
                        backgroundColor: "#fff3cd",
                        borderRadius: "5px",
                      }}
                    >
                      <p style={{ margin: "5px 0", fontSize: "0.9em" }}>
                        📅 <strong>Aucun paiement enregistré</strong>
                      </p>
                      <p
                        style={{
                          margin: "5px 0",
                          fontSize: "0.9em",
                          color: "#666",
                        }}
                      >
                        📅 <strong>Contrat débuté le:</strong>{" "}
                        {contractStartDate}
                      </p>
                    </div>
                  );
                }
              })()}

              <p>
                <strong>Technicien :</strong> {user.nom} {user.prenom}
              </p>
              <label>Consommation précédente (m³)</label>
              <input
                name="consommationPre"
                type="number"
                placeholder="Consommation précédente"
                value={form.consommationPre}
                onChange={handleChange}
                required
                min="0"
                disabled={editingConsommation && editingConsommation.status !== "init"}
                style={{
                  backgroundColor: editingConsommation && editingConsommation.status !== "init" ? '#f5f5f5' : 'white',
                  cursor: editingConsommation && editingConsommation.status !== "init" ? 'not-allowed' : 'text'
                }}
                title={editingConsommation && editingConsommation.status !== "init" 
                  ? "Modification autorisée uniquement pour les consommations avec statut Init" 
                  : "Modifier la consommation précédente si nécessaire"}
              />
              <label>Consommation actuelle (m³)</label>
              <input
                name="consommationActuelle"
                type="number"
                placeholder="Consommation actuelle"
                value={form.consommationActuelle}
                onChange={handleChange}
                required
                min="0"
                disabled={editingConsommation && editingConsommation.status !== "init"}
                style={{
                  backgroundColor: editingConsommation && editingConsommation.status !== "init" ? '#f5f5f5' : 'white',
                  cursor: editingConsommation && editingConsommation.status !== "init" ? 'not-allowed' : 'text'
                }}
                title={editingConsommation && editingConsommation.status !== "init" 
                  ? "Modification autorisée uniquement pour les consommations avec statut Init" 
                  : "Saisir la consommation actuelle"}
              />
              <select
                name="periode"
                value={form.periode}
                onChange={handleChange}
                required
                disabled={(() => {
                  const { availableMonths } = getAvailableMonths(showFormId);
                  return availableMonths.length === 0;
                })()}
                style={{
                  padding: "8px",
                  borderRadius: "4px",
                  border: "1px solid #ddd",
                  marginBottom: "10px",
                  width: "100%",
                  backgroundColor: (() => {
                    const { availableMonths } = getAvailableMonths(showFormId);
                    return availableMonths.length === 0 ? "#f5f5f5" : "white";
                  })(),
                }}
              >
                {(() => {
                  const { availableMonths } = getAvailableMonths(showFormId);

                  if (availableMonths.length === 0) {
                    return <option value="">Aucun mois disponible</option>;
                  }

                  return availableMonths.map((month) => {
                    const date = new Date(month + "-01");
                    const monthName = date.toLocaleDateString("fr-FR", {
                      year: "numeric",
                      month: "long",
                    });
                    return (
                      <option key={month} value={month}>
                        {monthName}
                      </option>
                    );
                  });
                })()}
              </select>
              <div className="consommation-modal-actions">
                <button
                  type="submit"
                  disabled={(() => {
                    const { availableMonths } = getAvailableMonths(showFormId);
                    return availableMonths.length === 0;
                  })()}
                  style={{
                    backgroundColor: (() => {
                      const { availableMonths } =
                        getAvailableMonths(showFormId);
                      return availableMonths.length === 0 ? "#cccccc" : "";
                    })(),
                    cursor: (() => {
                      const { availableMonths } =
                        getAvailableMonths(showFormId);
                      return availableMonths.length === 0
                        ? "not-allowed"
                        : "pointer";
                    })(),
                  }}
                >
                  Valider
                </button>
                <button type="button" onClick={() => setShowFormId(null)}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <footer className="page-footer">
        <p>© 2025 Abar. Powered by <strong>Jysser Connect</strong></p>
      </footer>
    </div>
  );
}

